<script setup lang="ts">
import { useWordProcessorStore } from "~/components/Base/WordProcessor/useWordProcessorStore";
import { getBodyParagraphCountMessage } from "~/composables/document-analysis/analyzeDocumentBlocks";

const documentId = inject("documentId") as any;

const documentStore = useSubmittedDocumentStore(documentId);
const { submittedDocument } = storeToRefs(documentStore);
const { access: writingToolsAccess } =
  useStudentWritingToolsAccess(documentId);

const documentViewModeStore = useDocumentViewModeStore(documentId);
const { isCompanionMode } = storeToRefs(documentViewModeStore);

const documentSelfAssessmentStore = useDocumentSelfAssessmentStore(documentId);
const { detectedBodyParagraphCount, selfAssessmentConfiguration } =
  storeToRefs(documentSelfAssessmentStore);

const documentChecklistStore = useDocumentChecklistStore(documentId);
const {
  numExpectedBodyParagraphs,
  useChecklist,
  useAllCriteriaInstances,
  useAllChecklistCriteria,
} = storeToRefs(documentChecklistStore);

const wordProcessorStore = useWordProcessorStore(documentId);

const message = ref("");

onMounted(() => {
  // Enable formatting
  wordProcessorStore.setShouldShowFormatting(true);
});

const markAsComplete = () => {
  message.value = getBodyParagraphCountMessage(
    detectedBodyParagraphCount.value,
    numExpectedBodyParagraphs.value
  );

  if (message.value != "") {
    return;
  }

  // Disable formatting
  wordProcessorStore.setShouldShowFormatting(false);

  documentSelfAssessmentStore.setActiveStepIndex(-1);
};

const isNavigatingBackToChecklist = ref(false);
const backToChecklist = async () => {
  isNavigatingBackToChecklist.value = true;
  useSubmittedDocumentStore(documentId).updateDocumentState(
    DocumentState.editing
  );
  await useSubmittedDocumentStore(documentId).saveDocumentDebounced();
};

const hasBibliographyStep = computed(() => {
  const bibliographyPattern = /bibliograph|works cited|works consulted/i;

  const containsBibliographyTerm = (
    value: unknown,
    visited = new WeakSet<object>()
  ): boolean => {
    if (typeof value === "string") {
      return bibliographyPattern.test(value);
    }

    if (Array.isArray(value)) {
      return value.some((item) => containsBibliographyTerm(item, visited));
    }

    if (value != undefined && typeof value === "object") {
      if (visited.has(value)) return false;
      visited.add(value);

      return Object.values(value).some((item) =>
        containsBibliographyTerm(item, visited)
      );
    }

    return false;
  };

  const hasMatchingSelfAssessmentStep =
    selfAssessmentConfiguration.value.steps.some((step) =>
      containsBibliographyTerm([
        step.identifier,
        step.label,
        step.hint,
        step.metricId,
        step.criteriaAttribute?.attribute,
        step.checklistCriteria?.label,
        step.checklistCriteria?.hint,
        step.checklistCriteria?.evaluationMetricId,
        step.instructions,
        step.checklistCriteria?.studentInstructions,
        step.checklistCriteria?.studentInstructionsOverrides,
      ])
    );

  if (hasMatchingSelfAssessmentStep) return true;

  const hasMatchingChecklistSection = (
    sections: NonNullable<typeof useChecklist.value>["sections"]
  ): boolean =>
    (sections ?? []).some(
      (section) =>
        containsBibliographyTerm(section.title) ||
        section.criteriaInstances.some((instance) =>
          containsBibliographyTerm(instance.labelOverride)
        ) ||
        hasMatchingChecklistSection(section.sections)
    );

  if (hasMatchingChecklistSection(useChecklist.value?.sections)) return true;

  if (
    useChecklist.value?.customCriteriaInstances?.some((instance) =>
      containsBibliographyTerm(instance.labelOverride)
    )
  ) {
    return true;
  }

  const activeCriteriaInstances = useAllCriteriaInstances.value.filter(
    (instance) => instance.ignore != true
  );
  const activeCriteriaIds = new Set(
    activeCriteriaInstances.map((instance) => instance.checklistCriteriaId)
  );

  if (
    activeCriteriaInstances.some((instance) =>
      containsBibliographyTerm(instance.labelOverride)
    )
  ) {
    return true;
  }

  return (useAllChecklistCriteria.value ?? []).some(
    (criteria) =>
      activeCriteriaIds.has(criteria.id) &&
      containsBibliographyTerm([
        criteria.label,
        criteria.hint,
        criteria.studentInstructions,
        criteria.studentInstructionsOverrides,
      ])
  );
});

const formattingSteps = computed(() => {
  const steps = [
    {
      identifier: "paragraph-begins-with-one-tab",
      hint: "Each paragraph begins with one tab.",
    },
    {
      identifier: "paragraph-ends-with-one-enter",
      hint: "Each paragraph ends with one red ¶.",
    },
    {
      identifier: "no-enters-within-paragraph",
      hint: "There are no extra red ¶ in your paragraphs.",
    },
  ];

  if (hasBibliographyStep.value) {
    steps.push({
      identifier: "bibliography-formatting",
      hint: "Your bibliography must follow a page break and begin with a centered title: Bibliography, Works Cited, or Works Consulted.",
    });
  }

  return steps;
});
</script>

<template>
  <div class="flex flex-col gap-2">
    <div
      class="flex flex-row items-center gap-2 border-b pb-1 pl-4 pr-2 pt-4 justify-between"
    >
      <h2 class="text-h5 font-bold">Initial Review</h2>
      <BaseTextButton
        v-if="
          submittedDocument?.enableStudentSubmissionCreation == true ||
          writingToolsAccess.canWrite == true ||
          isCompanionMode == true
        "
        :show-spinner="isNavigatingBackToChecklist"
        @click="backToChecklist"
      >
        <div class="flex flex-row items-center gap-1 text-body-2">
          <BaseIconListChecks />
          Back to Checklist
        </div>
      </BaseTextButton>
    </div>
    <div v-auto-animate class="px-4">
      <BaseLabelBadge v-if="message != ''" color="danger" class="mb-2">
        {{ message }}
      </BaseLabelBadge>

      <BaseLabelBadge class="mb-2">
        <div class="flex flex-col gap-2">
          <p>
            Look at your
            <span class="text-secondary">
              {{ numExpectedBodyParagraphs }}-paragraph
            </span>
            composition and check the following.
          </p>
          <ul class="flex flex-col gap-2 ml-1">
            <li
              v-for="step in formattingSteps"
              :key="step.identifier"
              class="flex flex-row items-start gap-1"
            >
              <span>✓</span>
              <span>
                {{ step.hint }}
              </span>
            </li>
          </ul>
        </div>
      </BaseLabelBadge>

      <div class="flex flex-row items-center justify-end">
        <BaseButton @click="markAsComplete">
          I have done this.
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
